import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { SettingsService } from './settings/settings.service';
import { AuthService } from './auth.service';
import { SessionMonitorService } from '../session/session-monitor.service';
import { LoginToRoomService } from './login-to-room.service';
import { AppLoadingService } from './app-loading.service';
import { BrowserDetectionService } from './browser-detection.service';
import { SubscriptionManagerService } from './subscription-manager/subscription-manager.service';
import { CommonModule } from '@angular/common';
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { DialogModule } from '@progress/kendo-angular-dialog';
import { TiLayoutModule } from '@app/extensions/kendo/kendo-angular-layout/ti.layout.module';
import { TreeViewModule } from '@progress/kendo-angular-treeview';
import { GridModule } from '@progress/kendo-angular-grid';
import { DefaultFormsService } from '@app/services/default-forms.service';
import { AlertComponent } from './dialogs/components/alert/alert.component';
import { ErrorsInformationComponent } from './dialogs/components/errors-information/errors-information.component';
import { ErrorsGridDialogComponent } from './dialogs/components/errors-grid/errors-grid-modal.component';
import { DialogService } from './dialogs/dialog.service';
import { BackgroundJobComponent } from './background-jobs/components/background-job/background-job.component';
import { PromptDialogComponent } from './dialogs/components/prompt/prompt.component';
import { QuestionComponent } from './dialogs/components/question/question.component';
import { SimpleComponentsModule } from '@app/shared/ui/simple-components/simple-components.module';
import { BackgroundJobsService } from './background-jobs/background-jobs.service';
import { NotificationsService } from './notifications/notifications.service';
import { BackgroundJobResultComponent } from './background-jobs/components/background-job-result/background-job-result.component';
import { PopupWindowService } from './popup-window.service';
import { EmailAttachmentLinksGeneratorService } from './communication/email-attachment-links-generator.service';
import { ContentViewerModule } from '@app/shared/misc/content-viewer/content-viewer.module';
import { IframeContentComponent } from '@app/services/dialogs/components/iframe-content/iframe-content.component';
import { SimpleCredentialsPromptComponent } from '@app/services/dialogs/components/simple-credentials-prompt/simple-credentials-prompt.component';
import { SpinnerModule } from '@app/shared/ui/simple-components/spinner/spinner.module';
import { ModalModule } from '@app/shared/dialogs/modal/modal.module';
import { DocumentViewerUrlService } from '@app/services/document-viewer-url.service';
import { RelatedDocumentsService } from '@app/services/related-documents.service';
import { AboutRoomModule } from '@app/pages/main/components/user-profile-details/room-about/about-room.module';
import { RelatedFieldsService } from '@app/services/related-fields.service';
import { InvSiteCustomFormService } from './inv-site-custom-form.service';
import { TopicTitlesService } from './topic-titles.service';
import { PipesModule } from '@app/shared/pipes/pipes.module';
import { PotentialDuplicatesService } from '@app/services/potential-duplicates.service';
import { DirectivesModule } from '@app/shared/directives/directives.module';
import { BasicSimpleCredentialsPromptComponent } from '@app/services/dialogs/components/simple-credentials-prompt/basic-simple-credentials-prompt.component';
import { ChangeRoleService } from '@app/session/change-role.service';
import { ProgressDialogComponent } from '@app/services/dialogs/components/progress/progress-dialog.component';
import { HtmlContentComponent } from '@app/shared/misc/html-content/html-content.component';
import { IframeBridgeService } from '@app/services/iframe-bridge.service';
import { IframeIntegrationBridgeService } from '@app/services/iframe-integration-bridge.service';
import { IframeBridgeDialogService, IFRAME_BRIDGE_REGISTRAR } from '@app/services/iframe-bridge-dialog.service';
import { BridgeServicesInitializerService } from '@app/services/bridge-services-initializer.service';
import { TooltipDirective as TPTTooltipDirective } from '@tpt/common/tooltips/tooltip';
import { TextBoxComponent } from "@progress/kendo-angular-inputs";

@NgModule({
  imports: [
    CommonModule,
    ButtonsModule,
    DialogModule,
    TiLayoutModule,
    TreeViewModule,
    GridModule,
    FormsModule,
    SimpleComponentsModule,
    ContentViewerModule,
    SpinnerModule,
    ModalModule,
    AboutRoomModule,
    PipesModule,
    DirectivesModule,
    HtmlContentComponent,
    TPTTooltipDirective,
    TextBoxComponent,
  ],
  declarations: [
    AlertComponent,
    PromptDialogComponent,
    ProgressDialogComponent,
    BasicSimpleCredentialsPromptComponent,
    SimpleCredentialsPromptComponent,
    QuestionComponent,
    ErrorsGridDialogComponent,
    ErrorsInformationComponent,
    BackgroundJobComponent,
    BackgroundJobResultComponent,
    IframeContentComponent,
  ],
})
export class ServicesModule {
  static forRoot(): ModuleWithProviders<ServicesModule> {
    return {
      ngModule: ServicesModule,
      providers: [
        SettingsService,
        AuthService,
        SessionMonitorService,
        LoginToRoomService,
        AppLoadingService,
        BrowserDetectionService,
        SubscriptionManagerService,
        NotificationsService,
        DialogService,
        DefaultFormsService,
        BackgroundJobsService,
        PopupWindowService,
        EmailAttachmentLinksGeneratorService,
        DocumentViewerUrlService,
        RelatedDocumentsService,
        PotentialDuplicatesService,
        RelatedFieldsService,
        InvSiteCustomFormService,
        TopicTitlesService,
        ChangeRoleService,
        IframeBridgeService,
        IframeBridgeDialogService,
        IframeIntegrationBridgeService,
        { provide: IFRAME_BRIDGE_REGISTRAR, useExisting: IframeIntegrationBridgeService, multi: true },
        BridgeServicesInitializerService,
      ],
    };
  }

  constructor(@Optional() @SkipSelf() servicesModule: ServicesModule) {
    if (servicesModule) {
      throw new Error('ServicesModule is already loaded. Import it in the SessionModule only');
    }
  }
}
